import {Children, cloneElement, FC, isValidElement, ReactElement, ReactNode} from 'react'
import {Navigate, Routes} from 'react-router-dom'
import {ModuleCode} from '../platform'
import {isModuleRouteEntitled} from '../platform/navigation'
import {isModulePrivateRoute, normalizeRouteDefinition} from '../product-modules'

const COMMON_PRIVATE_ROUTES = new Set([
  'module-dashboard/*',
  'auth/autologin/:token',
  'auth/*',
  'crafted/pages/profile/*',
  'crafted/account/*',
  '*',
])

type RouteElementProps = {
  children?: ReactNode
  element?: ReactNode
  path?: string
}

function isCommonPrivateRoute(path: string) {
  return COMMON_PRIVATE_ROUTES.has(normalizeRouteDefinition(path))
}

function filterRouteTree(
  children: ReactNode,
  moduleCode: ModuleCode,
  routeEntitlements: string[]
): ReactNode {
  return Children.map(children, (child) => {
    if (!isValidElement<RouteElementProps>(child)) {
      return child
    }

    const path = child.props.path
    if (path && !isCommonPrivateRoute(path)) {
      if (!isModulePrivateRoute(moduleCode, path)) {
        return null
      }

      if (!isModuleRouteEntitled(path, routeEntitlements)) {
        return cloneElement(child as ReactElement<RouteElementProps>, {
          element: <Navigate to='/error/403' replace />,
        })
      }
    }

    if (!child.props.children) {
      return child
    }

    return cloneElement(
      child as ReactElement<RouteElementProps>,
      undefined,
      filterRouteTree(child.props.children, moduleCode, routeEntitlements)
    )
  })
}

type ModuleScopedRoutesProps = {
  children: ReactNode
  moduleCode?: ModuleCode
  routeEntitlements?: string[]
}

/**
 * Leaves the legacy route tree untouched when no module is active. In a
 * module session it removes every route not owned by the active product
 * module before React Router builds its route configuration.
 */
export const ModuleScopedRoutes: FC<ModuleScopedRoutesProps> = ({
  children,
  moduleCode,
  routeEntitlements = [],
}) => {
  return (
    <Routes>
      {moduleCode ? filterRouteTree(children, moduleCode, routeEntitlements) : children}
    </Routes>
  )
}
